import React, { useState, useMemo } from 'react';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Typography, Chip, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, TextField, DialogActions, Paper
} from '@mui/material';
import {
  Edit, Delete, Save, ErrorOutline, Add, TabletAndroid
} from '@mui/icons-material';

// --- MOCK DATA: GLOBAL MASTER REGISTRY ---
const GLOBAL_MASTER = [
  { name: 'Auto_Sync_Interval', defaultValue: '300', description: 'Frequency of background data transmission (seconds)' },
  { name: 'Emergency_Cutoff_Enable', defaultValue: 'false', description: 'Enable hardware remote stop button on tablet' },
  { name: 'GPS_Drift_Threshold', defaultValue: '15', description: 'Minimum movement in meters to trigger GPS update' },
  { name: 'Session_Timeout', defaultValue: '120', description: 'Automatic logout time for inactive drivers (minutes)' },
];

// --- MOCK DATA: CUSTOMER SITE SETTINGS ---
const INITIAL_CUSTOMER_DATA = [
  { name: 'Auto_Sync_Interval', value: '600' }, 
  { name: 'GPS_Drift_Threshold', value: '15' }, 
];

export const TabletConfig = ({ organization }) => {
  const [customerSettings, setCustomerSettings] = useState(INITIAL_CUSTOMER_DATA);
  const [editModal, setEditModal] = useState({ open: false, data: null });

  // 1. SIMPLIFIED AUDIT ENGINE
  const auditData = useMemo(() => {
    // Map through global settings to see what the customer has or is missing
    const combined = GLOBAL_MASTER.map(masterItem => {
      const customerEntry = customerSettings.find(s => s.name === masterItem.name);
      
      return {
        ...masterItem,
        currentValue: customerEntry ? customerEntry.value : null,
        isMissing: !customerEntry
      };
    });

    // Also include any custom settings the customer has that aren't in the global master
    customerSettings.forEach(cs => {
      if (!GLOBAL_MASTER.find(m => m.name === cs.name)) {
        combined.push({
          name: cs.name,
          defaultValue: '---',
          description: 'Custom site-specific parameter',
          currentValue: cs.value,
          isMissing: false
        });
      }
    });

    return combined;
  }, [customerSettings]);

  // 2. HANDLERS
  const handleSaveSetting = () => {
    const { name, currentValue } = editModal.data;
    
    // Check if updating existing or adding new
    const exists = customerSettings.find(s => s.name === name);
    if (exists) {
      setCustomerSettings(customerSettings.map(s => s.name === name ? { ...s, value: currentValue } : s));
    } else {
      setCustomerSettings([...customerSettings, { name, value: currentValue }]);
    }
    
    setEditModal({ open: false, data: null });
  };

  const handleDelete = (name) => {
    setCustomerSettings(prev => prev.filter(s => s.name !== name));
  };

  const openAddModal = (row) => {
    // Pre-fill the modal with the Global Default value when adding a missing setting
    setEditModal({ 
      open: true, 
      data: { ...row, currentValue: row.defaultValue } 
    });
  };

  return (
    <Box>
      <Box sx={{  display: 'flex', justifyContent: 'space-between', alignItems: 'center',mb: 2, mt: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: -0.5 }}>
            Tablet Settings Registry
          </Typography>
     
        </Box>
        <Button variant="contained" startIcon={<Add />} size="small" >
          Add Setting
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1, border: '1px solid rgba(47, 114, 124, 0.2)' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, py: 2 }}>SETTING NAME</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>DESCRIPTION</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>GLOBAL DEFAULT</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>CUSTOMER VALUE</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, pr: 3 }}>ACTION</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {auditData.map((row) => (
              <TableRow 
                key={row.name} 
                hover 
                sx={{ bgcolor: row.isMissing ? 'rgba(211, 47, 47, 0.03)' : 'inherit' }}
              >
                <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace', color: 'primary.main' }}>
                  {row.name}
                </TableCell>
                
                <TableCell sx={{ maxWidth: 250 }}>
                  <Typography variant="caption" color="text.secondary">{row.description}</Typography>
                </TableCell>
                
                <TableCell sx={{ color: 'text.disabled', fontWeight: 600 }}>
                  {row.defaultValue}
                </TableCell>
                
                <TableCell>
                  {row.isMissing ? (
                    <Chip 
                      icon={<ErrorOutline style={{ fontSize: 16 }} />} 
                      label="Missing" 
                      size="small" 
                      color="error" 
                      sx={{ fontWeight: 700, borderRadius: 1.5 }} 
                    />
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                      {row.currentValue}
                    </Typography>
                  )}
                </TableCell>
                
                <TableCell align="right" sx={{ pr: 2 }}>
                  {row.isMissing ? (
                    // Explicit ADD button for missing parameters
                    <Button 
                      size="small" 
                      variant="outlined" 
                      color="error"
                      //startIcon={<Add />}
                      onClick={() => openAddModal(row)}
                      sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, py: 0.1 }}
                    >
                     + Add
                    </Button>
                  ) : (
                    // Edit/Delete for existing parameters
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Tooltip title="Edit Value">
                        <IconButton size="small" onClick={() => setEditModal({ open: true, data: row })}>
                          <Edit fontSize="small" sx={{ color: 'primary.main' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove Setting">
                        <IconButton size="small" color="error" onClick={() => handleDelete(row.name)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* --- ADD / EDIT MODAL --- */}
      <Dialog 
        open={editModal.open} 
        onClose={() => setEditModal({ open: false, data: null })}
        PaperProps={{ sx: { borderRadius: 4, width: 400 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {editModal.data?.isMissing ? 'Add Missing Setting' : 'Edit Setting'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Parameter: <strong style={{ color: '#000' }}>{editModal.data?.name}</strong>
          </Typography>

          <TextField
            fullWidth
            label="Customer Value"
            variant="outlined"
            size="small"
            value={editModal.data?.currentValue || ''}
            onChange={(e) => setEditModal({ 
              ...editModal, 
              data: { ...editModal.data, currentValue: e.target.value } 
            })}
            autoFocus
          />

          {editModal.data?.defaultValue !== '---' && (
            <Typography variant="caption" sx={{ mt: 1.5, display: 'block', color: 'text.disabled' }}>
              Global Default: {editModal.data?.defaultValue}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setEditModal({ open: false, data: null })} color="inherit">Cancel</Button>
          <Button 
            variant="contained" 
            startIcon={<Save />}
            onClick={handleSaveSetting}
            sx={{ borderRadius: 2 }}
          >
            Save Setting
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};